import { FormApi } from "final-form";
import _ from "lodash";
import React from "react";
import { Form } from "react-final-form";
import { useTranslation } from "react-i18next";
import { useUpdateProductMutation } from "src/ui/containers/dashboard/products/api";
import { Modal } from "src/ui/shared/Modal/Modal";
import { showErrorToast } from "src/ui/shared/toasts/Toasts";
import { useValidationSchema } from "src/ui/utils/useValidationSchema";
import { makeStyles } from "tss-react/mui";
import { z } from "zod";

import { useAllCurrentWorkspaceFiles } from "@dashboard/files/api/helpers";
import { FileContentType } from "@dashboard/files/types";
import { createSource } from "@dashboard/products/components/ProductBuilder/components/Recipes";
import { AndroidModule, Product } from "@dashboard/products/types";
import { useCurrentWorkspace } from "@dashboard/workspaces/api/helpers";
import { PermissionService } from "@services/permissionService/permissionService";
import { PrimaryButton } from "@shared/CustomButton";
import { ControlledSelectField } from "@shared/form/SelectField/SelectField";
import { Tooltip } from "@shared/Tooltip";

import SharedUtils from "./ModulePanel.utils";

const useStyles = makeStyles()((theme) => ({
    form: { display: "flex", flexDirection: "column", gap: theme.spacing(2), marginTop: theme.spacing(3) },
}));

/**
 * The file picker every module dialog is built around; a dialog adds its own fields on top of it.
 */
export type ModuleFileFormValues = {
    fileId: string;
};

type Props<Values extends ModuleFileFormValues, ModuleShape extends AndroidModule> = {
    product: Product | null;
    /**
     * The module to repoint, or null to add a new one.
     */
    module: ModuleShape | null;
    open: boolean;
    onToggle: (value: boolean) => void;
    title: string;
    description: string;
    /**
     * Replaces the file field's label, and explains the disabled save button, when the picker has nothing to offer.
     */
    noFilesAvailable: string;
    /**
     * Limits the picker to these file types; omit to offer every file in the workspace.
     */
    contentTypes?: FileContentType[];
    schema: z.ZodTypeAny;
    /**
     * Initial state of the dialog's own fields, read off the module being edited.
     */
    extraInitialValues?: Partial<Values>;
    /**
     * The module fields this dialog writes, applied on both the add and the edit path.
     */
    buildModuleValues: (file: API.File, sourceId: string, values: Values) => Partial<ModuleShape>;
    /**
     * What a newly added module needs on top of those fields — at least its type and tags.
     */
    newModuleDefaults: Partial<ModuleShape>;
    /**
     * The dialog's own fields, rendered under the file picker.
     */
    children?: React.ReactNode;
};

/**
 * The add-or-edit dialog behind every file-backed product module. With no `module` it appends a new one, with a
 * `module` it repoints that one, and it drops the source the module used to reference once nothing else needs it.
 */
export const ModuleFileDialog = <Values extends ModuleFileFormValues, ModuleShape extends AndroidModule>(
    props: Props<Values, ModuleShape>,
) => {
    const { classes } = useStyles();
    const { t } = useTranslation(["common"]);
    const validate = useValidationSchema(props.schema);

    const [updateProduct] = useUpdateProductMutation();

    const modules: AndroidModule[] = props.product?.customBuildConfig?.modules || [];
    const sources = props.product?.customBuildConfig?.sources || {};

    const editedModule: AndroidModule | null = props.module;
    const currentSourceId = typeof editedModule?.sourceId === "string" ? editedModule.sourceId : undefined;
    const currentFileId = currentSourceId ? sources[currentSourceId]?.fileId : undefined;

    const { data: workspace } = useCurrentWorkspace();
    const { data, isLoading, isError } = useAllCurrentWorkspaceFiles(
        props.contentTypes ? { contentTypes: props.contentTypes } : {},
        { skip: !props.open },
    );

    const accessibleFiles = data.filter(
        (item) => item.id === currentFileId || !SharedUtils.checkFileSourceExists(modules, sources, item.id),
    );
    const fileOptions = _.orderBy(
        accessibleFiles.map((item) => ({ label: item.filename, value: item.id })),
        "label",
    );

    // Falling back to the first file is right when adding, but on the edit path it would silently swap a module
    // whose file has since been deleted from the workspace — the card still shows the stored filename, so nothing
    // warns the user. Leave the field empty and let validation demand a deliberate pick.
    const initialFileId = editedModule
        ? fileOptions.find((option) => option.value === currentFileId)?.value
        : fileOptions[0]?.value;

    const { productAbility } = PermissionService();
    const cannotManageProducts = productAbility(workspace).cannot("manage", "Product");
    const formDisabled = isLoading || !fileOptions.length || cannotManageProducts || !props.product;

    const onSubmit = async (values: Values, form: FormApi<Values>) => {
        const file = data.find((item) => item.id === values.fileId);
        if (file && props.product) {
            const targetIndex = editedModule ? SharedUtils.findModuleIndex(modules, editedModule) : -1;
            if (editedModule && targetIndex === -1) {
                showErrorToast(t("common:error.products.moduleNoLongerInProduct"));
                return;
            }

            const [sourceId, updatedSources] = createSource(file, sources);
            const moduleValues = props.buildModuleValues(file, sourceId, values);
            const updatedModules: AndroidModule[] =
                targetIndex === -1
                    ? [...modules, { ...props.newModuleDefaults, ...moduleValues } as AndroidModule]
                    : modules.map((item, index) =>
                          index === targetIndex ? ({ ...item, ...moduleValues } as AndroidModule) : item,
                      );

            const newProduct = { ...props.product };
            if (newProduct.customBuildConfig) {
                newProduct.customBuildConfig = {
                    ...newProduct.customBuildConfig,
                    modules: updatedModules,
                    sources: SharedUtils.dropUnreferencedSource(updatedSources, updatedModules, currentSourceId, sourceId),
                };
            }

            const saved = await SharedUtils.saveProduct(updateProduct, newProduct);
            if (!saved) {
                return;
            }
        }
        form.restart();
        props.onToggle(false);
    };

    const getFilesUnavailableMessage = () => {
        if (isLoading || fileOptions.length) {
            return undefined;
        }
        if (isError) {
            return t("common:error.files.failedToFetchFiles");
        }
        return props.noFilesAvailable;
    };

    const getTooltipTitle = () => {
        const filesUnavailable = getFilesUnavailableMessage();
        if (filesUnavailable) {
            return filesUnavailable;
        }
        if (cannotManageProducts) {
            return t("common:tooltip.cannotManageProducts");
        }
        if (!props.product) {
            return t("common:tooltip.noProductSelected");
        }
        return undefined;
    };

    const tooltipTitle = getTooltipTitle();

    return (
        <Form<Values>
            onSubmit={onSubmit}
            validate={validate}
            initialValues={{ ...props.extraInitialValues, fileId: initialFileId } as Partial<Values>}
            render={({ handleSubmit, form, submitting }) => (
                <Modal
                    open={props.open}
                    onClose={() => {
                        form.restart();
                        props.onToggle(false);
                    }}
                    title={props.title}
                    description={props.description}
                    endButton={
                        <Tooltip hide={!tooltipTitle} title={tooltipTitle}>
                            <PrimaryButton onClick={form.submit} disabled={submitting || formDisabled} loading={submitting}>
                                {t("common:button.save")}
                            </PrimaryButton>
                        </Tooltip>
                    }
                >
                    <form onSubmit={handleSubmit} className={classes.form}>
                        <ControlledSelectField
                            name="fileId"
                            options={fileOptions}
                            disabled={formDisabled}
                            loading={isLoading}
                            label={getFilesUnavailableMessage() ?? t("common:label.file")}
                        />
                        {props.children}
                    </form>
                </Modal>
            )}
        />
    );
};
